import { useState } from "react";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";
import { useModal } from "../../contexts/ModalContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { motion } from "framer-motion";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const { showModal } = useModal();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const openQuickAccess = (mode) => {
    const user = mode === "professional"
      ? { id: 202, name: "Omar Nasser", email: "pro@preview.devgenius.com", role: "professional", profile: 2 }
      : { id: 101, name: "Aisha Al-Harthy", email: "business@preview.devgenius.com", role: "client", profile: 1 };

    login({ token: `preview-${mode}-token`, user });
    navigate(mode === "professional" ? "/ProProject" : "/BHome");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      showModal(t("invalidCredentials"), t("login"));
      setIsSubmitting(false);
    }, 300);
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      overflow: 'hidden',
    }}>
      {/* Floating geometric decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '200px',
        height: '200px',
        borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
        background: 'linear-gradient(135deg, rgba(var(--accent-primary-rgb), 0.08), rgba(124, 58, 237, 0.08))',
        animation: 'morph 8s ease-in-out infinite, floatSlow 10s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        right: '15%',
        width: '150px',
        height: '150px',
        borderRadius: '30% 60% 70% 40% / 50% 60% 30% 60%',
        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.06), rgba(245, 158, 11, 0.06))',
        animation: 'morph 10s ease-in-out infinite reverse, floatSlow 12s ease-in-out infinite reverse',
        pointerEvents: 'none',
      }} />
      
      {/* Orbital ring */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        border: '1px solid rgba(var(--accent-primary-rgb), 0.05)',
        animation: 'spin-slow 40s linear infinite',
        pointerEvents: 'none',
      }}>
        <div style={{
          position: 'absolute',
          top: '-4px',
          left: '50%',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'var(--accent-cyan)',
          opacity: 0.3,
        }} />
      </div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: '100%',
          maxWidth: '440px',
          background: 'var(--bg-card)',
          backdropFilter: 'var(--card-backdrop)',
          WebkitBackdropFilter: 'var(--card-backdrop)',
          border: '1px solid var(--border-glass)',
          borderRadius: '24px',
          padding: '48px 40px',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 2,
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Gradient border glow */}
        <div style={{
          position: 'absolute',
          inset: '-1px',
          borderRadius: '24px',
          padding: '1px',
          background: 'linear-gradient(135deg, rgba(0,212,255,0.2), transparent 40%, transparent 60%, rgba(124,58,237,0.2))',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        }} />

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: '36px' }}
        >
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: '1.5rem',
            color: '#fff',
            boxShadow: '0 8px 30px rgba(var(--accent-primary-rgb), 0.3)',
          }}>D</div>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.75rem',
            fontWeight: 700,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
          }}>{t("loginWelcomeBack")}</h2>
          <p style={{
            color: 'var(--text-muted)',
            fontSize: '0.9rem',
          }}>{t("loginContinue")}</p>
        </motion.div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <label style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}>
              {t("emailAddress")}
            </label>
            <input
              type="email"
              placeholder={t("emailPlaceholder")}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 18px',
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                borderRadius: '12px',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                fontFamily: 'var(--font-body)',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <label style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}>
              {t("password")}
            </label>
            <input
              type="password"
              placeholder={t("passwordPlaceholder")}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 18px',
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                borderRadius: '12px',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                fontFamily: 'var(--font-body)',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box',
              }}
            />
          </motion.div>

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(var(--accent-primary-rgb), 0.4)' }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            style={{
              width: '100%',
              padding: '14px',
              background: 'var(--gradient-primary)',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '0.95rem',
              fontWeight: 700,
              fontFamily: 'var(--font-body)',
              cursor: isSubmitting ? 'wait' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
              boxShadow: '0 4px 20px rgba(var(--accent-primary-rgb), 0.3)',
              marginTop: '4px',
            }}
          >
            {isSubmitting ? t("signingIn") : t("login")}
          </motion.button>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            margin: '8px 0',
          }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-glass)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {t("quickDemo")}
            </span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-glass)' }} />
          </div>

          {/* Demo buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <motion.button
              type="button"
              onClick={() => openQuickAccess("professional")}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              style={{
                flex: 1,
                padding: '12px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05))',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '12px',
                color: '#10b981',
                fontSize: '0.85rem',
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              {t("demoProfessional")}
            </motion.button>
            <motion.button
              type="button"
              onClick={() => openQuickAccess("business")}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              style={{
                flex: 1,
                padding: '12px',
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: '12px',
                color: '#f59e0b',
                fontSize: '0.85rem',
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              {t("demoBusiness")}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
